import { hashKey, useQueries, useQuery } from '@tanstack/react-query';
import { chunk, isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { CATCHUP_EXPANDED_QUERY_GC_TIME_IN_MILLIS, DATA_REFRESH_RATE_IN_MILLIS_LONG } from 'src/utils/constants/constants';
import {
  CatalogClient,
  CatalogResults,
  Feature,
  OnSaleEventQuery,
} from 'src/WebApiController';

import { useUserHasFeature } from '../useUserHasFeature';

export const useOnSaleEvents = (
  query: OnSaleEventQuery | undefined,
  disable = false,
  eventOnly = false
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { searchText, ...rest } = query ?? {};

  const { trackError } = useClientLogContext();

  const hasEventDiscoveryCapacityAndPrimaryFeature = useUserHasFeature(
    Feature.EventDiscoveryCapacityAndPrimary
  );
  const hasHideEventDiscoveryDemandDataFeature = useUserHasFeature(
    Feature.HideEventDiscoveryDemandData
  );

  const shouldQueryForSearch = useMemo(
    () =>
      !disable &&
      activeAccountWebClientConfig.activeAccountId != null &&
      query != null &&
      searchText != null,
    [activeAccountWebClientConfig.activeAccountId, disable, searchText, query]
  );
  const searchEventsQuery = useQuery({
    queryKey: [
      'searchEvents',
      activeAccountWebClientConfig.activeAccountId,
      searchText,
      disable,
    ],
    queryFn: () => {
      if (!shouldQueryForSearch || !query) {
        return null;
      }
      return new CatalogClient(activeAccountWebClientConfig).searchOnSaleEvents(
        query
      );
    },
    enabled: shouldQueryForSearch,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.searchOnSaleEvents', error, {
          searchText,
        });
      },
    },
  });

  const shouldQueryOnSaleEvents = useMemo(
    () =>
      !disable &&
      activeAccountWebClientConfig.activeAccountId != null &&
      !shouldQueryForSearch &&
      query != null &&
      rest != null,
    [
      activeAccountWebClientConfig.activeAccountId,
      disable,
      query,
      rest,
      shouldQueryForSearch,
    ]
  );
  const onSaleEventsQuery = useQuery({
    queryKey: [
      'getCatalogForOnSaleEvents',
      activeAccountWebClientConfig.activeAccountId,
      hashKey([rest]),
      disable,
    ],
    queryFn: () => {
      if (!shouldQueryOnSaleEvents) {
        return null;
      }
      return new CatalogClient(
        activeAccountWebClientConfig
      ).getCatalogForOnSaleEvents(query!);
    },

    enabled: shouldQueryOnSaleEvents,
    staleTime: Infinity,
    gcTime: CATCHUP_EXPANDED_QUERY_GC_TIME_IN_MILLIS,
    refetchInterval: DATA_REFRESH_RATE_IN_MILLIS_LONG,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.getCatalogForOnSaleEvents', error, {
          query,
        });
      },
    },
  });

  const isLoading = useMemo(
    () => onSaleEventsQuery.isLoading || searchEventsQuery.isLoading,
    [onSaleEventsQuery.isLoading, searchEventsQuery.isLoading]
  );

  const onSaleEvents = useMemo((): CatalogResults | null => {
    const events = searchEventsQuery.data ?? onSaleEventsQuery.data ?? null;
    for (const key in events?.events ?? {}) {
      if (isNaN(parseInt(key))) {
        delete events?.events[key];
      }
    }
    return events;
  }, [onSaleEventsQuery.data, searchEventsQuery.data]);

  const onSaleEventIds = useMemo(
    () =>
      Object.keys(onSaleEvents?.events ?? {})
        .map((id) => parseInt(id))
        .filter((id) => !isNaN(id)),
    [onSaleEvents]
  );

  const shouldToLoadMetrics = useMemo(() => {
    if (disable || eventOnly) {
      return false;
    }
    return true;
  }, [disable, eventOnly]);

  const shouldAggregateQuery = useMemo(
    () =>
      query != null &&
      onSaleEventIds.length > 0 &&
      !!activeAccountWebClientConfig.activeAccountId &&
      hasHideEventDiscoveryDemandDataFeature &&
      shouldToLoadMetrics,
    [
      activeAccountWebClientConfig.activeAccountId,
      hasHideEventDiscoveryDemandDataFeature,
      onSaleEventIds.length,
      query,
      shouldToLoadMetrics,
    ]
  );
  const metricsQuery = useQuery({
    queryKey: [
      'getCatalogOnSaleEventMetrics',
      activeAccountWebClientConfig.activeAccountId,
      onSaleEventIds,
      shouldToLoadMetrics,
    ],
    queryFn: async () => {
      if (!shouldAggregateQuery || !query) {
        return null;
      }

      const input: OnSaleEventQuery = {
        ...query,
        eventIds: Array.from(onSaleEventIds),
      };
      return await new CatalogClient(
        activeAccountWebClientConfig
      ).getCatalogOnSaleEventMetrics(input);
    },

    enabled: shouldAggregateQuery,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.getCatalogOnSaleEventMetrics', error, {
          eventIds: onSaleEventIds,
        });
      },
    },
  });

  const queries = useMemo(() => {
    const client = new CatalogClient(activeAccountWebClientConfig);

    return {
      queries: chunk(onSaleEventIds, 1000).map((idGroup) => {
        const shouldQuery =
          query != null &&
          shouldToLoadMetrics &&
          idGroup.length > 0 &&
          !!activeAccountWebClientConfig.activeAccountId &&
          !hasHideEventDiscoveryDemandDataFeature;

        return {
          queryKey: [
            'getCatalogOnSaleEventMetrics',
            activeAccountWebClientConfig.activeAccountId,
            hashKey([idGroup]),
            shouldToLoadMetrics,
          ],
          queryFn: async () => {
            if (!shouldQuery || !query) {
              return null;
            }

            const input: OnSaleEventQuery = {
              ...query,
              eventIds: Array.from(idGroup),
            };

            return await client.getCatalogOnSaleEventMetrics(input);
          },
          enabled: shouldQuery,
          refetchOnWindowFocus: false,
          onError: (error: ErrorTypes) => {
            trackError('CatalogClient.getCatalogOnSaleEventMetrics', error, {
              eventIds: idGroup,
            });
          },
        };
      }),
    };
  }, [
    activeAccountWebClientConfig,
    onSaleEventIds,
    shouldToLoadMetrics,
    hasHideEventDiscoveryDemandDataFeature,
    query,
    trackError,
  ]);

  const metricsQueriesFromHook = useQueries(queries);

  const shouldQueryEventAttributes = useMemo(
    () =>
      shouldToLoadMetrics &&
      hasEventDiscoveryCapacityAndPrimaryFeature &&
      activeAccountWebClientConfig.activeAccountId != null &&
      !isEmpty(onSaleEventIds),
    [
      activeAccountWebClientConfig.activeAccountId,
      hasEventDiscoveryCapacityAndPrimaryFeature,
      onSaleEventIds,
      shouldToLoadMetrics,
    ]
  );

  const eventAttributesQuery = useQuery({
    queryKey: [
      'getEventAttributes',
      activeAccountWebClientConfig.activeAccountId,
      onSaleEventIds,
      hasEventDiscoveryCapacityAndPrimaryFeature,
      shouldToLoadMetrics,
    ],
    queryFn: () => {
      if (!shouldQueryEventAttributes) {
        return null;
      }
      return new CatalogClient(activeAccountWebClientConfig).getEventAttributes(
        onSaleEventIds
      );
    },
    enabled: shouldQueryEventAttributes,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatalogClient.getEventAttributes', error, {
          onSaleEventIds,
        });
      },
    },
  });

  const onSaleEventMetrics = useMemo(() => {
    if (hasHideEventDiscoveryDemandDataFeature) {
      return metricsQuery.data ?? [];
    }
    return metricsQueriesFromHook.flatMap((q) => q.data ?? []);
  }, [
    hasHideEventDiscoveryDemandDataFeature,
    metricsQuery.data,
    metricsQueriesFromHook,
  ]);

  return {
    onSaleEvents,
    onSaleEventMetrics,
    eventAttributes: eventAttributesQuery.data,
    isLoading,
  };
};
